import React, { useState } from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { Heading, H2, Reviews, Button } from '../../components';

import css from './ListingPage.module.css';

const SectionReviews = props => {
  const intl = useIntl();
  const { reviews, fetchReviewsError, listing, currentUser, onUpdateListing } = props;
  const [showAllReviews, setShowAllReviews] = useState(false);

  const reviewCount = reviews.length;
  const hasReviews = reviewCount > 0;

  // Calculate average rating
  const averageRating = hasReviews 
    ? (reviews.reduce((sum, review) => sum + review.attributes.rating, 0) / reviewCount)
    : 0;

  // Determine how many reviews to show
  const MAX_INITIAL_REVIEWS = 3;
  const reviewsToShow = showAllReviews ? reviews : reviews.slice(0, MAX_INITIAL_REVIEWS);
  const hasMoreReviews = reviewCount > MAX_INITIAL_REVIEWS;

  const reviewsSummary = hasReviews
    ? intl.formatMessage(
        { id: 'ListingPage.reviewsSummary' },
        { rating: averageRating.toFixed(1), count: reviewCount }
      )
    : null;

  const showAllButtonText = intl.formatMessage(
    { id: 'ListingPage.showAllReviews' },
    { count: reviewCount }
  );

  // Handle provider response submission
  const handleResponseSubmit = async (reviewId, responseText) => {
    if (!onUpdateListing || !listing) {
      return Promise.reject(new Error('Unable to update listing'));
    }

    const currentResponses = listing.attributes?.publicData?.reviewResponses || {};
    const updatedResponses = {
      ...currentResponses,
      [reviewId]: {
        text: responseText,
        date: new Date().toISOString(),
      }
    };

    const updatedPublicData = {
      ...listing.attributes.publicData,
      reviewResponses: updatedResponses,
    };

    // Call the listing update handler passed from parent
    return onUpdateListing(listing.id.uuid, { publicData: updatedPublicData });
  };

  return (
    <section className={css.sectionReviews}>
      <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
        Reviews{hasReviews ? ` (${reviewCount})` : ''}
      </Heading>
      
      {hasReviews && (
        <div className={css.reviewsSummary}>
          {reviewsSummary}
        </div>
      )}
      
      {fetchReviewsError ? (
        <H2 className={css.errorText}>
          <FormattedMessage id="ListingPage.reviewsError" />
        </H2>
      ) : hasReviews ? (
        <>
          <Reviews 
            reviews={reviewsToShow} 
            listing={listing}
            currentUser={currentUser}
            onResponseSubmit={handleResponseSubmit}
          />
          {hasMoreReviews && !showAllReviews && (
            <div className={css.showAllReviewsButton}>
              <Button 
                type="button"
                onClick={() => setShowAllReviews(true)}
                size="small"
                variant="secondary"
              >
                {showAllButtonText}
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className={css.noReviews}>
          <FormattedMessage id="ListingPage.noReviews" />
        </div>
      )}
    </section>
  );
};

export default SectionReviews;
